/**
 * Per-game vertical rail. Two icon buttons that navigate to the Rosters
 * or Box-score full-page routes. Routes (`/games/:pk/rosters` and
 * `.../boxscore`) are the authoritative entry points; the SideNav is the
 * in-game shortcut. Modal mode was removed because the full-page layout
 * was always preferred — cramped modals over the field hurt readability.
 */
import { useNavigate } from 'react-router-dom';
import styles from './SideNav.module.css';

interface SideNavProps {
  gamePk: number | undefined;
  /** Propagated so subpages of a historical game stay in historical mode
   *  (one-shot snapshot) instead of silently opening a live SSE stream. */
  mode?: 'live' | 'historical';
}

export default function SideNav({ gamePk, mode = 'live' }: SideNavProps): JSX.Element | null {
  const navigate = useNavigate();
  if (!gamePk) return null;
  const modeSuffix = mode === 'historical' ? '?mode=historical' : '';

  return (
    <nav className={styles.rail} aria-label="Game tools" data-testid="side-nav">
      <button
        type="button"
        className={styles.btn}
        onClick={() => navigate(`/games/${gamePk}/rosters${modeSuffix}`)}
        aria-label="Rosters"
        data-testid="side-nav-rosters"
      >
        <RosterIcon />
        <span className={styles.label}>Rosters</span>
      </button>
      <button
        type="button"
        className={styles.btn}
        onClick={() => navigate(`/games/${gamePk}/boxscore${modeSuffix}`)}
        aria-label="Box score"
        data-testid="side-nav-boxscore"
      >
        <BoxIcon />
        <span className={styles.label}>Box score</span>
      </button>
    </nav>
  );
}

function RosterIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.icon}>
      <circle cx="9" cy="8" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3.5 19c0-3.2 2.4-5.6 5.5-5.6S14.5 15.8 14.5 19" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="17" cy="9.5" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M14 19c0-2.3 1.8-4.2 3.7-4.2 1.5 0 2.8.9 3.3 2.2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function BoxIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.icon}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <line x1="3.5" y1="10" x2="20.5" y2="10" stroke="currentColor" strokeWidth="1.6" />
      <line x1="3.5" y1="14.5" x2="20.5" y2="14.5" stroke="currentColor" strokeWidth="1.6" />
      <line x1="9.5" y1="4.5" x2="9.5" y2="19.5" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}
